import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import {
  Row,
  Col,
  Button,
  Space,
  DatePicker,
  Empty,
  Select,
} from "antd";
import { ReloadOutlined, FullscreenOutlined } from "@ant-design/icons";
import FSMServiceRequestPipeline from "./FSMDashboardParts/FSMServiceRequestPipeline";
import FSMFormMetrics from "./FSMDashboardParts/FSMFormMetrics";
import FSMPerformanceTrends from "./FSMDashboardParts/FSMPerformanceTrends";
import FSMAlertsSummary from "./FSMDashboardParts/FSMAlertsSummary";
import FSMResourceStatus from "./FSMDashboardParts/FSMResourceStatus";
import FSMTaskDistribution from "./FSMDashboardParts/FSMTaskDistribution";
import FSMMonthlyComparison from "./FSMDashboardParts/FSMMonthlyComparison";
import FSMRetailAnalytics from "./FSMDashboardParts/FSMRetailAnalytics";
import RetailComparePicker from "./FSMDashboardParts/RetailComparePicker";
import {
  currentMonthKey,
  isComparableMonth,
  resolveComparisonRanges,
  shiftMonthKey,
} from "./FSMDashboardParts/retail/retailCompare";
import trans from "../../utils";
import dayjs from "dayjs";
import usePermission from "../../hooks/usePermission";

const SECTION_STORAGE_KEY = "fsm-dashboard-visible-sections-v1";
// Only the on/off choice is stored; months live in the URL so a stored
// absolute month never goes stale and links can be shared.
const RETAIL_COMPARE_STORAGE_KEY = "fsm-retail-compare-v1";
const RETAIL_COMPARE_PARAM = "retailCompare";
const utcCalendarToday = () => dayjs(new Date().toISOString().slice(0, 10));

const readStoredCompareEnabled = () => {
  try {
    return localStorage.getItem(RETAIL_COMPARE_STORAGE_KEY) === "1";
  } catch (_error) {
    return false;
  }
};

// "?retailCompare=2026-10,2026-09" → { monthA: "2026-10", monthB: "2026-09" }
const parseCompareParam = (value) => {
  const [monthA, monthB] = String(value || "").split(",");
  return /^\d{4}-\d{2}$/.test(monthA || "") && /^\d{4}-\d{2}$/.test(monthB || "")
    ? { monthA, monthB }
    : null;
};

const readStoredSections = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SECTION_STORAGE_KEY));
    return Array.isArray(stored) ? stored : null;
  } catch (_error) {
    return null;
  }
};

export default function FSMDashboard() {
  const { view: canViewServiceRequest } = usePermission(
    "REPORTSFSSERVICEREQUEST",
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs().endOf("month"));
  const [retailDates, setRetailDates] = useState(() => {
    const todayUtc = utcCalendarToday();
    return [
      todayUtc.subtract(29, "day").startOf("day"),
      todayUtc.endOf("day"),
    ];
  });
  const [selectedSections, setSelectedSections] = useState(readStoredSections);
  const [searchParams, setSearchParams] = useSearchParams();
  // Months are null until the user picks them, meaning "current month vs the
  // month before"; those defaults roll over by themselves at month end.
  const [retailCompare, setRetailCompare] = useState(() => {
    const months = parseCompareParam(searchParams.get(RETAIL_COMPARE_PARAM));
    return {
      enabled: Boolean(months) || readStoredCompareEnabled(),
      monthA: months?.monthA || null,
      monthB: months?.monthB || null,
    };
  });

  const sectionOptions = [
    { value: "alerts", label: trans("Alerts summary") },
    { value: "resources", label: trans("Resource status") },
    { value: "tasks", label: trans("Task distribution") },
    { value: "forms", label: trans("Form metrics") },
    ...(canViewServiceRequest
      ? [
          {
            value: "serviceRequests",
            label: trans("Service request pipeline"),
          },
        ]
      : []),
    { value: "performance", label: trans("Performance trends") },
    { value: "monthly", label: trans("Monthly comparison") },
    { value: "retail", label: trans("Retail analytics") },
  ];
  const authorizedSectionKeys = sectionOptions.map((option) => option.value);
  const visibleSectionKeys = (selectedSections || authorizedSectionKeys).filter(
    (key) => authorizedSectionKeys.includes(key),
  );
  const isSectionVisible = (key) => visibleSectionKeys.includes(key);
  const retailToday = utcCalendarToday();
  const retailMinimumDate = retailToday.subtract(3, "month").startOf("day");
  const retailTodayIso = retailToday.format("YYYY-MM-DD");
  const thisMonthKey = currentMonthKey(retailTodayIso);
  const isUsableMonth = (key) => isComparableMonth(key, retailTodayIso);
  const compareMonthA = isUsableMonth(retailCompare.monthA)
    ? retailCompare.monthA
    : thisMonthKey;
  const defaultCompareMonthB = isUsableMonth(shiftMonthKey(compareMonthA, -1))
    ? shiftMonthKey(compareMonthA, -1)
    : shiftMonthKey(compareMonthA, 1);
  const compareMonthB =
    isUsableMonth(retailCompare.monthB) && retailCompare.monthB !== compareMonthA
      ? retailCompare.monthB
      : defaultCompareMonthB;
  const retailComparisonRanges = retailCompare.enabled
    ? resolveComparisonRanges({
        monthA: compareMonthA,
        monthB: compareMonthB,
        todayIso: retailTodayIso,
      })
    : null;
  const retailPeriod = retailComparisonRanges
    ? [
        dayjs(retailComparisonRanges.a.fromDate),
        dayjs(retailComparisonRanges.a.toDate),
      ]
    : retailDates;
  const retailComparison = retailComparisonRanges
    ? {
        fromDate: dayjs(retailComparisonRanges.b.fromDate),
        toDate: dayjs(retailComparisonRanges.b.toDate),
        monthKey: retailComparisonRanges.b.monthKey,
        sameDays: retailComparisonRanges.sameDays,
        // A period that includes today keeps changing; a closed one does not.
        isOpen: retailComparisonRanges.b.isCurrentMonth,
      }
    : null;

  useEffect(() => {
    if (selectedSections === null) return;
    try {
      localStorage.setItem(
        SECTION_STORAGE_KEY,
        JSON.stringify(selectedSections),
      );
    } catch (_error) {
      // The dashboard remains usable when browser storage is unavailable.
    }
  }, [selectedSections]);

  useEffect(() => {
    try {
      localStorage.setItem(
        RETAIL_COMPARE_STORAGE_KEY,
        retailCompare.enabled ? "1" : "0",
      );
    } catch (_error) {
      // Comparison still works for this visit without browser storage.
    }
  }, [retailCompare.enabled]);

  // Mirror explicitly chosen months into the URL (and drop the parameter for
  // defaults or when comparison is off) without adding history entries.
  const compareParamValue =
    retailCompare.enabled && (retailCompare.monthA || retailCompare.monthB)
      ? `${compareMonthA},${compareMonthB}`
      : null;
  useEffect(() => {
    const currentValue = searchParams.get(RETAIL_COMPARE_PARAM);
    if ((currentValue || null) === compareParamValue) return;
    const nextParams = new URLSearchParams(searchParams);
    if (compareParamValue) nextParams.set(RETAIL_COMPARE_PARAM, compareParamValue);
    else nextParams.delete(RETAIL_COMPARE_PARAM);
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareParamValue]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(
      () => {
        setRefreshKey((prev) => prev + 1);
        setLastUpdated(new Date());
      },
      5 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    setFromDate(dayjs().startOf("month"));
    setToDate(dayjs().endOf("month"));
    setLastUpdated(new Date());
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  const handleRetailDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setRetailDates([dates[0].startOf("day"), dates[1].endOf("day")]);
    }
  };
  const handleRetailCompareChange = (change) => {
    setRetailCompare((current) => {
      const next = {
        ...current,
        monthA: change.monthA ?? current.monthA ?? compareMonthA,
        monthB: change.monthB ?? current.monthB ?? compareMonthB,
      };
      // Picking the comparison month as the selected month moves the
      // comparison to the month before (or after, at the window's edge).
      if (next.monthA === next.monthB) {
        const previous = shiftMonthKey(next.monthA, -1);
        next.monthB = isUsableMonth(previous)
          ? previous
          : shiftMonthKey(next.monthA, 1);
      }
      return next;
    });
  };

  const standardSections = [
    {
      key: "resources",
      content: (
        <FSMResourceStatus
          key={`resource-${refreshKey}`}
          refreshKey={refreshKey}
        />
      ),
    },
    {
      key: "tasks",
      content: (
        <FSMTaskDistribution
          key={`distribution-${refreshKey}`}
          fromDate={fromDate}
          toDate={toDate}
        />
      ),
    },
    {
      key: "forms",
      content: (
        <FSMFormMetrics
          key={`form-metrics-${refreshKey}`}
          fromDate={fromDate}
          toDate={toDate}
        />
      ),
    },
    ...(canViewServiceRequest
      ? [
          {
            key: "serviceRequests",
            content: (
              <FSMServiceRequestPipeline
                key={`pipeline-${refreshKey}`}
                fromDate={fromDate}
                toDate={toDate}
              />
            ),
          },
        ]
      : []),
    {
      key: "performance",
      content: (
        <FSMPerformanceTrends
          key={`trends-${refreshKey}`}
          refreshKey={refreshKey}
        />
      ),
    },
    {
      key: "monthly",
      content: (
        <FSMMonthlyComparison
          key={`monthly-${refreshKey}`}
          refreshKey={refreshKey}
        />
      ),
    },
  ].filter((section) => isSectionVisible(section.key));

  const standardSectionRows = [];
  for (let index = 0; index < standardSections.length; index += 2) {
    standardSectionRows.push(standardSections.slice(index, index + 2));
  }

  return (
    <div>
      <Helmet>
        <title>Field Service Management Dashboard</title>
        <meta
          name="description"
          content="Comprehensive FSM Dashboard for managing field operations, technicians, and service requests"
        />
      </Helmet>

      {/* Dashboard Header */}
      <div
        className="d-flex justify-content-between align-items-center flex-wrap mb-4 p-3"
        style={{ background: "#FFFFFFDE" }}
      >
        <div>
          <h2 className="fs-24 fw-700 neutral-800 mb-1">
            {trans("Field Service Management Dashboard")}
          </h2>
          <p className="fs-14 neutral-500 mb-0">
            {trans("Last updated:")} {lastUpdated.toLocaleTimeString()}
          </p>
        </div>
        <Space wrap size={[12, 10]} style={{ justifyContent: "flex-end" }}>
          {isSectionVisible("retail") ? (
            <Space size={[12, 6]} wrap>
              <RetailComparePicker
                enabled={retailCompare.enabled}
                monthA={compareMonthA}
                monthB={compareMonthB}
                todayIso={retailTodayIso}
                onToggle={(enabled) =>
                  setRetailCompare((current) => ({ ...current, enabled }))
                }
                onChange={handleRetailCompareChange}
              />
              {!retailCompare.enabled ? (
                <DatePicker.RangePicker
                  allowClear={false}
                  value={retailDates}
                  onChange={handleRetailDateChange}
                  disabledDate={(current) =>
                    current &&
                    (current.isBefore(retailMinimumDate, "day") ||
                      current.isAfter(retailToday, "day"))
                  }
                  format="DD MMM YYYY"
                  aria-label={trans("Retail period")}
                  style={{ width: 270 }}
                />
              ) : null}
            </Space>
          ) : null}
          <Select
            mode="multiple"
            value={visibleSectionKeys}
            options={sectionOptions}
            onChange={(values) =>
              setSelectedSections(
                values.filter((key) => authorizedSectionKeys.includes(key)),
              )
            }
            optionFilterProp="label"
            maxTagCount="responsive"
            placeholder={trans("Select dashboard sections")}
            aria-label={trans("Visible dashboard sections")}
            style={{ minWidth: 280, maxWidth: 420 }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            type="default"
          >
            {trans("Refresh")}
          </Button>
          <Button
            icon={<FullscreenOutlined />}
            onClick={toggleFullscreen}
            type="default"
          >
            {isFullscreen ? trans("Exit Fullscreen") : trans("Fullscreen")}
          </Button>
        </Space>
      </div>

      {/* Alert Summary - Top Priority */}
      <div style={{ background: "#FFFFFFDE" }} className="p-3">
        {isSectionVisible("alerts") ? (
          <div className="mb-4">
            <FSMAlertsSummary
              key={`alerts-${refreshKey}`}
              refreshKey={refreshKey}
              canViewServiceRequest={canViewServiceRequest}
            />
          </div>
        ) : null}

        {standardSectionRows.map((sections, rowIndex) => (
          <Row
            gutter={[16, 16]}
            className="mb-4"
            key={`dashboard-row-${rowIndex}`}
          >
            {sections.map((section) => (
              <Col
                xs={24}
                lg={sections.length === 1 ? 24 : 12}
                key={section.key}
              >
                {section.content}
              </Col>
            ))}
          </Row>
        ))}

        {isSectionVisible("retail") ? (
          <FSMRetailAnalytics
            refreshKey={refreshKey}
            fromDate={retailPeriod[0]}
            toDate={retailPeriod[1]}
            comparison={retailComparison}
          />
        ) : null}

        {!visibleSectionKeys.length ? (
          <Empty description={trans("No dashboard sections selected")} />
        ) : null}
      </div>
    </div>
  );
}
